import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import AssignSchool from './component'

const AssignSchoolContainer = ({ onSelection, siteData }) => {
  const [localState, setLocalState] = useState({
    hasRegionData: false, // Start as false, will be updated when regions are loaded
    netSuiteCustomerData: [],
    isInitialized: false, // Track if component has been initialized with data
  })

  // Consolidated handler for all form changes
  const handleFormChange = useCallback(
    (event, type = 'field') => {
      const { name, value, options, selectedIndex } = event.target

      if (type === 'school') {
        const text = options[selectedIndex].innerHTML
        const selectedNSCustomer = localState.netSuiteCustomerData.find(
          nsc => nsc.id === parseInt(value, 10),
        )

        onSelection({
          netSuiteCustomerId: selectedNSCustomer
            ? selectedNSCustomer.id
            : parseInt(value, 10),
          nsCustomerId: selectedNSCustomer?.nsCustomerId || '',
          siteName:
            selectedNSCustomer?.id === 0
              ? ''
              : selectedNSCustomer?.companyName || text,
        })
      } else if (type === 'regionData') {
        setLocalState(prev => ({ ...prev, hasRegionData: value.length > 0 }))
        return value
      } else if (type === 'netSuiteData') {
        setLocalState(prev => ({ ...prev, netSuiteCustomerData: value }))
        return value
      } else {
        // Handle location field changes (country, region, city)
        // Only reset dependent fields if this is a user-initiated change
        const resetFields = {
          city: ['netSuiteCustomerId', 'nsCustomerId', 'siteName'],
          region: ['netSuiteCustomerId', 'nsCustomerId', 'siteName', 'city'],
          country: [
            'netSuiteCustomerId',
            'nsCustomerId',
            'siteName',
            'city',
            'region',
          ],
        }

        const newState = {
          [name]: value,
          // Only reset fields if component is initialized (user is making changes)
          ...(localState.isInitialized
            ? Object.fromEntries(
                resetFields[name]?.map(field => [field, '']) || [],
              )
            : {}),
        }

        onSelection(newState)

        // Mark as initialized after first user interaction
        if (!localState.isInitialized) {
          setLocalState(prev => ({ ...prev, isInitialized: true }))
        }
      }
    },
    [localState.netSuiteCustomerData, localState.isInitialized, onSelection],
  )

  // Initialize hasRegionData based on incoming siteData
  useEffect(() => {
    if (siteData.country && !localState.isInitialized) {
      // If we have a country but no region, assume no regions exist initially
      // The hook will update this when the regions API responds
      setLocalState(prev => ({
        ...prev,
        hasRegionData: false, // Start as false, will be updated by the hook
        isInitialized: true, // Mark as initialized when we have site data
      }))
    }
  }, [siteData.country, siteData.region, localState.isInitialized])

  return (
    <AssignSchool
      hasRegionData={localState.hasRegionData}
      siteData={siteData}
      onFormChange={handleFormChange}
    />
  )
}

AssignSchoolContainer.propTypes = {
  onSelection: PropTypes.func.isRequired,
  siteData: PropTypes.shape({
    country: PropTypes.string,
    region: PropTypes.string,
    city: PropTypes.string,
    netSuiteCustomerId: PropTypes.number,
    nsCustomerId: PropTypes.string,
    siteName: PropTypes.string,
  }),
}

AssignSchoolContainer.defaultProps = {
  siteData: {},
}

AssignSchoolContainer.displayName = 'AssignSchool'

export default AssignSchoolContainer
